import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Save, Send, RotateCcw, Languages } from "lucide-react";
import { calculateQuote, getPricing, saveQuote, getEmailStatus, formatUSD } from "@/lib/api";
import HandoffDialog from "@/components/HandoffDialog";

const Section = ({ index, title, children }) => (
  <section className="border-t border-[#050505] py-8 first:border-t-0 first:pt-0">
    <div className="flex items-baseline gap-4 mb-6">
      <span className="font-mono text-xs text-[#525252]">{String(index).padStart(2, "0")}</span>
      <h3 className="font-display text-lg font-bold uppercase tracking-tight">{title}</h3>
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">{children}</div>
  </section>
);

const Field = ({ label, children, testid }) => (
  <label className="block" data-testid={testid}>
    <span className="swiss-label">{label}</span>
    {children}
  </label>
);

const Select = ({ value, onChange, options, testid }) => (
  <select className="swiss-input" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}>
    {options.map(([k, label]) => (
      <option key={k} value={k}>
        {label}
      </option>
    ))}
  </select>
);

const NumberInput = ({ value, onChange, min = 1, max = 100000, step = 1, testid }) => (
  <input
    type="number"
    className="swiss-input"
    value={value}
    min={min}
    max={max}
    step={step}
    onChange={(e) => onChange(Number(e.target.value || 0))}
    data-testid={testid}
  />
);

const Row = ({ label, value, sub, bold, accent }) => (
  <div className="flex items-baseline justify-between py-2 border-b border-[#e5e5e5] last:border-b-0">
    <div>
      <div className={`${bold ? "text-sm font-bold" : "text-xs"} ${accent ? "text-[#FF3333]" : ""} uppercase tracking-[0.16em]`}>
        {label}
      </div>
      {sub && <div className="text-[10px] text-[#525252] uppercase tracking-[0.18em] mt-0.5">{sub}</div>}
    </div>
    <div className={`font-mono ${bold ? "text-base font-bold" : "text-sm"} ${accent ? "text-[#FF3333]" : ""}`}>
      {value}
    </div>
  </div>
);

export default function Calculator() {
  const navigate = useNavigate();
  const [pricing, setPricing] = useState(null);
  const initialSpec = {
    trim_size: "228x152mm (SA std)",
    page_count: 230,
    color_mode: "bw",
    paper_type: "70gsm White Bond",
    cover_type: "softcover_250",
    binding_type: "pur",
    finishing_type: "gloss",
    quantity: 100,
  };
  const initialClient = {
    project_title: "",
    client_name: "",
    client_email: "",
    notes: "",
  };
  const [spec, setSpec] = useState(initialSpec);
  const [client, setClient] = useState(initialClient);
  const [language, setLanguage] = useState("en");
  const [calc, setCalc] = useState(null);
  const [calcLoading, setCalcLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [transferring, setTransferring] = useState(false);
  const [emailStatus, setEmailStatus] = useState(null);
  const [handoffQuote, setHandoffQuote] = useState(null);

  useEffect(() => {
    getEmailStatus().then(setEmailStatus).catch(() => setEmailStatus({ configured: false }));
  }, []);

  useEffect(() => {
    getPricing()
      .then((p) => {
        setPricing(p);
        // ensure spec defaults exist
        setSpec((s) => ({
          ...s,
          trim_size: Object.keys(p.trim_sizes).includes(s.trim_size) ? s.trim_size : Object.keys(p.trim_sizes)[0],
          paper_type: Object.keys(p.paper_types).includes(s.paper_type) ? s.paper_type : Object.keys(p.paper_types)[0],
          cover_type: Object.keys(p.cover_types).includes(s.cover_type) ? s.cover_type : Object.keys(p.cover_types)[0],
          binding_type: Object.keys(p.binding_types).includes(s.binding_type) ? s.binding_type : Object.keys(p.binding_types)[0],
          finishing_type: Object.keys(p.finishing_types).includes(s.finishing_type) ? s.finishing_type : Object.keys(p.finishing_types)[0],
        }));
      })
      .catch(() => toast.error("Failed to load pricing settings"));
  }, []);

  // live calculation, debounced
  useEffect(() => {
    if (!pricing) return;
    setCalcLoading(true);
    const handle = setTimeout(() => {
      calculateQuote(spec)
        .then((r) => setCalc(r))
        .catch((e) => {
          const msg = e?.response?.data?.detail || "Pricing failed";
          toast.error(msg);
        })
        .finally(() => setCalcLoading(false));
    }, 250);
    return () => clearTimeout(handle);
  }, [spec, pricing]);

  const options = useMemo(() => {
    if (!pricing) return null;
    return {
      trim: Object.entries(pricing.trim_sizes).map(([k, v]) => [k, k]),
      paper: Object.entries(pricing.paper_types).map(([k, v]) => [k, `${k} · $${Number(v.rate_per_page).toFixed(3)}/pg`]),
      color: Object.entries(pricing.color_modes).map(([k, v]) => [k, v.label]),
      cover: Object.entries(pricing.cover_types).map(([k, v]) => [k, `${v.label} · $${Number(v.unit_cost).toFixed(2)}`]),
      binding: Object.entries(pricing.binding_types).map(([k, v]) => [k, `${v.label} · $${Number(v.unit_cost).toFixed(2)}`]),
      finishing: Object.entries(pricing.finishing_types).map(([k, v]) => [k, `${v.label}${Number(v.unit_cost) > 0 ? ` · +$${Number(v.unit_cost).toFixed(2)}` : ""}`]),
    };
  }, [pricing]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const q = await saveQuote({ spec, ...client, language });
      toast.success(`Quote ${q.quote_number} saved`);
      navigate(`/quotes/${q.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleTransfer = async () => {
    setTransferring(true);
    try {
      const q = await saveQuote({ spec, ...client, language });
      toast.success(`Quote ${q.quote_number} saved — choose a hand-off action`);
      setHandoffQuote(q);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setTransferring(false);
    }
  };

  const handleClear = () => {
    if (!window.confirm("Clear all fields and start over?")) return;
    setSpec(initialSpec);
    setClient(initialClient);
    setLanguage("en");
    toast.success("Form cleared");
  };

  if (!pricing || !options) {
    return (
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-8 lg:px-12 py-24 text-center">
        <Loader2 className="w-6 h-6 animate-spin mx-auto mb-4" strokeWidth={1.5} />
        <div className="text-sm uppercase tracking-[0.2em] text-[#525252]">Loading…</div>
      </div>
    );
  }

  return (
    <div className="max-w-screen-2xl mx-auto px-4 sm:px-8 lg:px-12 py-12 lg:py-16" data-testid="calculator-page">
      <header className="mb-10 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6">
        <div>
          <div className="text-label mb-4" data-testid="calc-eyebrow">№ 002 — Build a Quote</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-none">
            Book Quote Calculator
          </h1>
          <p className="mt-4 text-base text-[#525252] max-w-2xl">
            Specify every parameter. Pricing updates live on the right.
          </p>
        </div>

        <div className="flex flex-col items-start sm:items-end gap-2" data-testid="language-picker">
          <div className="text-[11px] uppercase tracking-[0.2em] text-[#525252] inline-flex items-center gap-1.5">
            <Languages className="w-3.5 h-3.5" strokeWidth={1.5} />
            Quote language
          </div>
          <div className="flex">
            {[
              { code: "en", label: "English" },
              { code: "fr", label: "Français" },
            ].map((opt) => (
              <button
                key={opt.code}
                onClick={() => setLanguage(opt.code)}
                data-testid={`lang-${opt.code}`}
                className={`px-4 h-10 text-[11px] uppercase tracking-[0.18em] font-bold border border-[#050505] transition-colors ${
                  language === opt.code
                    ? "bg-[#050505] text-white"
                    : "bg-white text-[#050505] hover:bg-[#f5f5f5]"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#525252]">
            Used for PDF, email & client page
          </div>
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-0 border border-[#050505]">
        {/* LEFT: FORM */}
        <div className="lg:col-span-8 p-6 sm:p-8 lg:p-12 lg:border-r border-[#050505]">
          <Section index={1} title="Size & Pages">
            <Field label="Trim size" testid="field-trim">
              <Select value={spec.trim_size} onChange={(v) => setSpec({ ...spec, trim_size: v })} options={options.trim} testid="select-trim-size" />
            </Field>
            <Field label="Page count" testid="field-pages">
              <NumberInput value={spec.page_count} onChange={(v) => setSpec({ ...spec, page_count: v })} min={1} max={10000} testid="input-page-count" />
            </Field>
          </Section>

          <Section index={2} title="Paper & Color">
            <Field label="Interior paper" testid="field-paper">
              <Select value={spec.paper_type} onChange={(v) => setSpec({ ...spec, paper_type: v })} options={options.paper} testid="select-paper-type" />
            </Field>
            <Field label="Interior color" testid="field-color">
              <Select value={spec.color_mode} onChange={(v) => setSpec({ ...spec, color_mode: v })} options={options.color} testid="select-color-mode" />
            </Field>
          </Section>

          <Section index={3} title="Cover & Binding">
            <Field label="Cover type" testid="field-cover">
              <Select value={spec.cover_type} onChange={(v) => setSpec({ ...spec, cover_type: v })} options={options.cover} testid="select-cover-type" />
            </Field>
            <Field label="Binding" testid="field-binding">
              <Select value={spec.binding_type} onChange={(v) => setSpec({ ...spec, binding_type: v })} options={options.binding} testid="select-binding-type" />
            </Field>
          </Section>

          <Section index={4} title="Finishing & Quantity">
            <Field label="Cover finishing" testid="field-finishing">
              <Select value={spec.finishing_type} onChange={(v) => setSpec({ ...spec, finishing_type: v })} options={options.finishing} testid="select-finishing-type" />
            </Field>
            <Field label="Print run (qty)" testid="field-qty">
              <NumberInput value={spec.quantity} onChange={(v) => setSpec({ ...spec, quantity: v })} min={1} max={100000} testid="input-quantity" />
            </Field>
          </Section>

          <Section index={5} title="Client (optional)">
            <Field label="Project title" testid="field-project">
              <input className="swiss-input" value={client.project_title} onChange={(e) => setClient({ ...client, project_title: e.target.value })} placeholder="The Atlas of Lost Cities" data-testid="input-project-title" />
            </Field>
            <Field label="Client name" testid="field-client-name">
              <input className="swiss-input" value={client.client_name} onChange={(e) => setClient({ ...client, client_name: e.target.value })} placeholder="Jane Doe" data-testid="input-client-name" />
            </Field>
            <Field label="Client email" testid="field-client-email">
              <input type="email" className="swiss-input" value={client.client_email} onChange={(e) => setClient({ ...client, client_email: e.target.value })} placeholder="jane@example.com" data-testid="input-client-email" />
            </Field>
            <Field label="Notes" testid="field-notes">
              <input className="swiss-input" value={client.notes} onChange={(e) => setClient({ ...client, notes: e.target.value })} placeholder="Special instructions…" data-testid="input-notes" />
            </Field>
          </Section>
        </div>

        {/* RIGHT: BREAKDOWN */}
        <aside className="lg:col-span-4 bg-[#F5F5F5] p-6 sm:p-8 lg:p-10 lg:sticky lg:top-[88px] self-start" data-testid="breakdown-panel">
          <div className="flex items-center justify-between mb-2">
            <div className="text-label">Live Breakdown</div>
            {calcLoading && <Loader2 className="w-3.5 h-3.5 animate-spin text-[#525252]" strokeWidth={1.5} />}
          </div>

          <div className="border-y border-[#050505] py-6 my-4">
            <div className="text-[11px] uppercase tracking-[0.2em] text-[#525252] mb-2">Client Price (Total)</div>
            <div className="font-display font-black text-5xl lg:text-6xl tracking-tighter leading-none text-[#FF3333] font-mono" data-testid="total-amount">
              {calc ? formatUSD(calc.breakdown.total) : "—"}
            </div>
            <div className="mt-3 text-xs font-mono text-[#525252]">
              {calc ? (
                <>
                  <span data-testid="unit-price">{formatUSD(calc.breakdown.unit_price)}</span> / book ·{" "}
                  <span data-testid="qty-display">{calc.breakdown.quantity}</span> copies
                </>
              ) : "—"}
            </div>
          </div>

          {calc && (
            <>
              {/* Printer cost block */}
              <div className="mb-5" data-testid="printer-cost-block">
                <div className="flex items-baseline justify-between mb-2">
                  <div className="text-label">Printer Cost</div>
                  <div className="font-mono text-[11px] text-[#525252]">
                    {formatUSD(calc.breakdown.printer_unit_cost)} / book
                  </div>
                </div>
                <Row label="Interior" sub={`${spec.page_count} pp · ${calc.labels.color_mode}`} value={formatUSD(calc.breakdown.interior_cost)} />
                <Row label="Cover" sub={calc.labels.cover_type} value={formatUSD(calc.breakdown.cover_cost)} />
                <Row label="Binding" sub={calc.labels.binding_type} value={formatUSD(calc.breakdown.binding_cost)} />
                <Row label="Finishing" sub={calc.labels.finishing_type} value={formatUSD(calc.breakdown.finishing_cost)} />
                <Row label="Setup" sub="one-time" value={formatUSD(calc.breakdown.setup_fee)} />
                <Row label="Proof" sub="one-time" value={formatUSD(calc.breakdown.proof_fee)} />
                <Row
                  label="Shipping"
                  sub={calc.breakdown.estimated_weight_kg ? `${calc.breakdown.estimated_weight_kg.toFixed(2)} kg total` : "weight-based"}
                  value={formatUSD(calc.breakdown.courier_fee)}
                />
                {calc.breakdown.discount_pct > 0 && (
                  <Row label={`Volume discount (${calc.breakdown.discount_pct}%)`} value={`-${formatUSD(calc.breakdown.discount_amount)}`} accent />
                )}
                <div className="pt-2 mt-2 border-t border-[#050505]">
                  <Row label="Printer total" value={formatUSD(calc.breakdown.printer_subtotal)} bold />
                </div>
              </div>

              {/* Markup & client */}
              <div className="bg-white border border-[#050505] p-4" data-testid="markup-block">
                <Row label={`Markup (${calc.breakdown.markup_pct}%)`} sub="your profit" value={`+${formatUSD(calc.breakdown.markup_amount)}`} accent />
                {calc.breakdown.tax_rate > 0 && (
                  <Row label={`Tax (${calc.breakdown.tax_rate}%)`} value={formatUSD(calc.breakdown.tax_amount)} />
                )}
                <div className="pt-2 mt-2 border-t border-[#050505]">
                  <Row label="Client total" value={formatUSD(calc.breakdown.total)} bold />
                  <Row
                    label="Your profit"
                    sub={`${calc.breakdown.profit_pct}% of revenue`}
                    value={formatUSD(calc.breakdown.profit_amount)}
                    bold
                    accent
                  />
                </div>
              </div>
            </>
          )}

          <div className="mt-8 space-y-2">
            <button
              onClick={handleTransfer}
              disabled={!calc || transferring || saving}
              className="btn-primary w-full justify-center"
              data-testid="transfer-btn"
              title="Save and choose how to deliver"
            >
              {transferring ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" strokeWidth={1.5} />}
              {transferring ? "Saving…" : "Save & Hand-off"}
            </button>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={handleSave}
                disabled={!calc || saving || transferring}
                className="btn-secondary w-full justify-center"
                data-testid="save-quote-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" strokeWidth={1.5} />}
                {saving ? "Saving…" : "Save Only"}
              </button>
              <button
                onClick={handleClear}
                disabled={saving || transferring}
                className="btn-secondary w-full justify-center"
                data-testid="clear-form-btn"
              >
                <RotateCcw className="w-4 h-4" strokeWidth={1.5} /> Clear
              </button>
            </div>
          </div>
          <div className="mt-3 text-[11px] uppercase tracking-[0.18em] text-[#525252] text-center">
            All prices in USD · Markup editable in Pricing Settings
          </div>
        </aside>
      </div>

      {handoffQuote && (
        <HandoffDialog
          quote={handoffQuote}
          emailStatus={emailStatus}
          onClose={() => {
            setHandoffQuote(null);
            navigate(`/quotes/${handoffQuote.id}`);
          }}
        />
      )}
    </div>
  );
}
